import { TimeView } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';

import { DiallingHours } from '~pages/CampaignManagement/domain';

// Luxon weekday numbers (1 = Monday ... 7 = Sunday) to the keys used by DiallingHours.diallingDays.
const weekdayNumberToDay: { [key: number]: string } = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

const toMinuteOfDay = (hour: number, minute: number) => hour * 60 + minute;

export interface DiallingWindow {
  /** Minutes from midnight, inclusive. */
  start: number;
  /** Minutes from midnight, exclusive. */
  end: number;
}

/**
 * The callable window for the day `date` falls on, as minutes from midnight.
 *
 * `end` is exclusive, matching the existing form validation which treats the end minute as the
 * first minute that is no longer callable.
 *
 * Returns undefined when there is nothing to restrict:
 *  - no dialling hours configured for the campaign (we assume everything is fair game),
 *  - no hours configured for that weekday (whole day is already blocked by shouldDisableDate),
 *  - a window that does not end after it starts. Overnight windows have never been supported by
 *    the form validation either, and restricting on one would disable every time of day, so the
 *    picker stays open and validation remains the only gate.
 */
export const getDiallingWindow = (
  diallingHours: DiallingHours | undefined,
  date: DateTime,
): DiallingWindow | undefined => {
  if (diallingHours === undefined) {
    return undefined;
  }

  const diallingDay = diallingHours.diallingDays[weekdayNumberToDay[date.weekday]];

  // Typed as always present, but days without configured hours come back null.
  if (diallingDay === null || diallingDay === undefined) {
    return undefined;
  }

  const start = toMinuteOfDay(diallingDay.startTimeHour, diallingDay.startTimeMin);
  const end = toMinuteOfDay(diallingDay.endTimeHour, diallingDay.endTimeMin);

  if (end <= start) {
    return undefined;
  }

  return { start, end };
};

/**
 * Builds a `shouldDisableTime` handler for a DateTimePicker that should only offer times inside
 * a campaign's dialling hours.
 *
 * The picker calls this once per option in the currently visible column, passing the candidate
 * date with that column's value applied and every other part taken from the current selection.
 * The hours column therefore has to be judged on the whole hour rather than the exact candidate:
 * for a window opening at 09:30, hour 09 combined with a currently selected minute of 00 is
 * outside the window, and testing it directly would make the opening hour unselectable.
 */
export const buildShouldDisableTime =
  (diallingHours: DiallingHours | undefined) =>
  (value: DateTime, view: TimeView): boolean => {
    const window = getDiallingWindow(diallingHours, value);

    if (window === undefined) {
      return false;
    }

    switch (view) {
      case 'hours': {
        // Disable the hour only when none of its minutes are callable.
        const hourStart = toMinuteOfDay(value.hour, 0);
        return hourStart >= window.end || hourStart + 59 < window.start;
      }
      case 'minutes': {
        const minuteOfDay = toMinuteOfDay(value.hour, value.minute);
        return minuteOfDay < window.start || minuteOfDay >= window.end;
      }
      default:
        // Seconds are not shown by any of the callback pickers.
        return false;
    }
  };
